import { memo, useEffect, useRef } from 'react';
import { faCode } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { Icon } from '@/components/ui/icon';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { useMounted } from '@/hooks/use-mounted';
import { isHostedVideoProviderUrl } from '@/lib/embed/hosted-video-url';
import { normalizeTrustedElfsightHtml } from '@/lib/embed/trusted-elfsight';
import { normalizeEmbedUrl } from '@/lib/embed-utils';
import { readNumber, readRecord, readString } from '@/lib/prop-utils';
import { addWindowEventListener, cn } from '@/lib/utils';
import { getDragThumbnailClassName } from '../editor/dnd/drag-visuals';

interface EmbedBlockProps {
  html?: string;
  resolvedHtml?: string;
  url?: string;
  interactive?: boolean;
}

const MIN_EMBED_HEIGHT_PX = 100;
const DEFAULT_EMBED_PLACEHOLDER_HEIGHT_PX = 320;
const MAX_NORMALIZED_EMBED_HEIGHT_PX = 10_000;
const CALENDLY_DEFAULT_HEIGHT_PX = 700;
const URL_PARSE_BASE = 'https://local.embed';

interface EmbedProviderRule {
  name: string;
  matches: (hostname: string) => boolean;
  fallbackHeight?: string;
}

const EMBED_PROVIDER_RULES: EmbedProviderRule[] = [
  {
    name: 'calendly',
    matches: (hostname) => hostname === 'calendly.com' || hostname.endsWith('.calendly.com'),
    fallbackHeight: `${CALENDLY_DEFAULT_HEIGHT_PX}px`,
  },
];

function readEmbedHeightMessage(data: unknown): number | null {
  const height = readNumber(readRecord(data)?.llEmbedHeight);
  if (height === undefined || !Number.isFinite(height) || height <= 0) {
    return null;
  }

  return Math.ceil(height);
}

// A frame from another site cannot be measured from this page, so its height has
// to be guessed. Calendly is the exception: it sends its own height in a message,
// which lets the frame fit its content instead of sitting in a fixed box with
// empty space below it.
function readCalendlyHeightMessage(data: unknown): number | null {
  const record = readRecord(data);
  if (record?.event !== 'calendly.page_height') {
    return null;
  }

  const height = readString(readRecord(record.payload)?.height);
  const match = height?.trim().match(/^(\d+(?:\.\d+)?)px$/i);
  if (!match) {
    return null;
  }

  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? Math.ceil(parsed) : null;
}

function normalizeIframeHeightPx(height: number): number | null {
  if (!Number.isFinite(height) || height <= 0) {
    return null;
  }

  const cappedHeight = Math.min(height, MAX_NORMALIZED_EMBED_HEIGHT_PX);
  return Math.max(Math.ceil(cappedHeight), MIN_EMBED_HEIGHT_PX);
}

function applyIframeHeight(iframe: HTMLIFrameElement, height: number): void {
  const normalizedHeight = normalizeIframeHeightPx(height);
  if (normalizedHeight == null) {
    return;
  }

  iframe.style.height = `${normalizedHeight}px`;
}

function attachIframePostMessageResize(iframe: HTMLIFrameElement): () => void {
  const removeMessageListener = addWindowEventListener<MessageEvent>('message', (event) => {
    if (event.source !== iframe.contentWindow) {
      return;
    }

    const height = readEmbedHeightMessage(event.data) ?? readCalendlyHeightMessage(event.data);
    if (height != null) {
      applyIframeHeight(iframe, height);
    }
  });

  return () => {
    removeMessageListener?.();
  };
}

function normalizeIframeHeight(height: string | undefined): string | null {
  if (!height) {
    return null;
  }

  const heightMatch = height.trim().match(/^(\d+(?:\.\d+)?)(px)?$/i);
  if (!heightMatch) {
    return null;
  }

  const normalizedHeight = normalizeIframeHeightPx(Number(heightMatch[1]));
  return normalizedHeight != null ? `${normalizedHeight}px` : null;
}

function resolveInitialIframeHeight(height: string | undefined, fallbackHeight?: string): string {
  return (
    normalizeIframeHeight(height) ??
    normalizeIframeHeight(fallbackHeight) ??
    `${DEFAULT_EMBED_PLACEHOLDER_HEIGHT_PX}px`
  );
}

function readStyleHeight(style: string | null): string | undefined {
  if (!style) {
    return undefined;
  }

  for (const declaration of style.split(';')) {
    const match = declaration.trim().match(/^height\s*:\s*(.+)$/i);
    if (match?.[1]) {
      return match[1].trim() || undefined;
    }
  }

  return undefined;
}

function EmbedPlaceholder({ message }: { message: string }) {
  return (
    <div
      className="flex w-full items-center justify-center rounded-md border border-dashed border-border px-4 text-center text-sm text-muted-foreground"
      style={{ minHeight: `${DEFAULT_EMBED_PLACEHOLDER_HEIGHT_PX}px` }}
    >
      <Icon icon={faCode} className="mr-2 size-4 shrink-0" />
      {message}
    </div>
  );
}

const SAFE_IFRAME_ATTRS = ['src', 'width', 'height', 'title', 'allow', 'allowfullscreen'] as const;
type SafeIframeAttrs = Partial<Record<(typeof SAFE_IFRAME_ATTRS)[number], string>>;
type ExternalIframeAttrs = SafeIframeAttrs & { src: string };

interface ParsedExternalIframeEmbed {
  kind: 'external';
  attrs: ExternalIframeAttrs;
  declaredHeight?: string;
  providerFallbackHeight?: string;
}

interface ParsedSrcDocIframeEmbed {
  kind: 'srcDoc';
  html: string;
  height?: string;
  title?: string;
}

type ParsedIframeEmbed = ParsedExternalIframeEmbed | ParsedSrcDocIframeEmbed;

interface InjectedIframeEmbedProps {
  attrs: ExternalIframeAttrs;
  declaredHeight?: string;
  providerFallbackHeight?: string;
  interactive: boolean;
  isPageEditor: boolean;
  dragThumbnailClassName?: string;
}

interface SrcDocIframeProps {
  html: string;
  interactive: boolean;
  initialHeight?: string;
  title?: string;
  // Only ever set for embeds from a vendor we have checked, such as a cleaned
  // Elfsight widget. Arbitrary pasted HTML must never be given this, because it
  // grants the embed access to the page around it.
  allowSameOrigin?: boolean;
}

const SRCDOC_SANDBOX_LOCKED =
  'allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms allow-presentation';
const SRCDOC_SANDBOX_TRUSTED_VENDOR =
  'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms allow-presentation';

function isSafeEmbedUrl(src: string): boolean {
  try {
    const normalized = src.startsWith('//') ? `https:${src}` : src;
    const url = new URL(normalized);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
    return url.hostname.length > 0 && url.hostname.includes('.');
  } catch {
    return false;
  }
}

function readEmbedHostname(src: string): string | null {
  try {
    return new URL(src, URL_PARSE_BASE).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function findEmbedProviderRule(src: string): EmbedProviderRule | undefined {
  const hostname = readEmbedHostname(src);
  if (!hostname) {
    return undefined;
  }

  return EMBED_PROVIDER_RULES.find((rule) => rule.matches(hostname));
}

function readExternalEmbedFallbackHeight(src: string): string | undefined {
  return findEmbedProviderRule(src)?.fallbackHeight;
}

function parseIframeEmbed(html: string): ParsedIframeEmbed | null {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const iframe = doc.querySelector('iframe');
    if (!iframe) return null;

    const bodyChildren = Array.from(doc.body.childNodes).filter((node) => {
      if (node.nodeType !== Node.TEXT_NODE) {
        return true;
      }

      return node.textContent?.trim().length;
    });

    const srcDoc = iframe.getAttribute('srcdoc');
    if (srcDoc) {
      return {
        kind: 'srcDoc',
        html: srcDoc,
        height: iframe.getAttribute('height') ?? undefined,
        title: iframe.getAttribute('title') ?? undefined,
      };
    }

    const isStandaloneIframeSnippet =
      bodyChildren.length === 1 && bodyChildren[0] === iframe && iframe.parentElement === doc.body;
    if (!isStandaloneIframeSnippet) {
      return null;
    }

    const attrs = Object.fromEntries(
      SAFE_IFRAME_ATTRS.flatMap((attr) => {
        const value = iframe.getAttribute(attr);
        return value != null ? [[attr, value]] : [];
      }),
    ) as SafeIframeAttrs;
    const declaredHeight =
      iframe.getAttribute('height') ?? readStyleHeight(iframe.getAttribute('style'));
    const providerFallbackHeight = attrs.src
      ? readExternalEmbedFallbackHeight(attrs.src)
      : undefined;

    return attrs.src && isSafeEmbedUrl(attrs.src)
      ? {
          kind: 'external',
          attrs: attrs as ExternalIframeAttrs,
          declaredHeight,
          providerFallbackHeight,
        }
      : null;
  } catch {
    return null;
  }
}

// A frame pointing at another site cannot be measured, so its stated height is
// taken as given and the frame is left at that size.
function InjectedIframeEmbed({
  attrs,
  declaredHeight,
  providerFallbackHeight,
  interactive,
  dragThumbnailClassName,
}: InjectedIframeEmbedProps) {
  const isHostedVideoEmbed = isHostedVideoProviderUrl(attrs.src);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || isHostedVideoEmbed) {
      return;
    }

    return attachIframePostMessageResize(iframe);
  }, [isHostedVideoEmbed]);

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-md',
        isHostedVideoEmbed && 'aspect-video',
        dragThumbnailClassName,
      )}
    >
      <iframe
        ref={iframeRef}
        src={attrs.src}
        width={attrs.width}
        title={attrs.title ?? 'Embedded content'}
        allow={attrs.allow}
        allowFullScreen={attrs.allowfullscreen != null}
        className={cn(
          'block h-full w-full rounded-md',
          !isHostedVideoEmbed && 'min-h-[100px]',
          !interactive && 'pointer-events-none',
        )}
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms allow-presentation allow-storage-access-by-user-activation"
        referrerPolicy="no-referrer-when-downgrade"
        style={
          isHostedVideoEmbed
            ? { overflow: 'hidden' }
            : {
                height: resolveInitialIframeHeight(declaredHeight, providerFallbackHeight),
                minHeight: `${MIN_EMBED_HEIGHT_PX}px`,
                overflow: 'hidden',
              }
        }
      />
    </div>
  );
}

const SRCDOC_RESIZE_SCRIPT = `<script>
(function(){
  function readHeight(){
    var body=document.body;
    if(!body)return 0;
    // Measured from the body, which reflects the content, rather than the document,
    // which always reports the frame's current size. Using the document would make
    // the frame able to grow but never shrink.
    return Math.max(body.scrollHeight,body.offsetHeight,Math.ceil(body.getBoundingClientRect().height));
  }
  function postHeight(){
    var h=readHeight();
    if(h>0)parent.postMessage({llEmbedHeight:h},"*");
  }
  new ResizeObserver(postHeight).observe(document.documentElement);
  new MutationObserver(postHeight).observe(document.documentElement,{subtree:true,childList:true,attributes:true,characterData:true});
  window.addEventListener("resize",postHeight);
  window.addEventListener("load",postHeight,{once:true});
  postHeight();
})();
</script>`;

// The default body margin is removed so the measured height matches the content
// exactly; otherwise the browser's eight pixels are counted and then clipped.
const SRCDOC_STYLE_RESET = '<style>html,body{margin:0}</style>';

function wrapSrcDocHtml(html: string): string {
  return SRCDOC_STYLE_RESET + SRCDOC_RESIZE_SCRIPT + html;
}

// Pasted HTML runs cut off from the page around it, so it cannot read the
// visitor's cookies, stored data, or anything else on the page. Checked vendor
// embeds are the exception, as their scripts do not work without that access.
// Either way the height comes back as a message rather than being read directly.
function SrcDocIframe({
  html,
  interactive,
  initialHeight,
  title,
  allowSameOrigin = false,
}: SrcDocIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    return attachIframePostMessageResize(iframe);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={wrapSrcDocHtml(html)}
      className={cn(
        'w-full rounded-md border-0 overflow-hidden',
        !interactive && 'pointer-events-none',
      )}
      sandbox={allowSameOrigin ? SRCDOC_SANDBOX_TRUSTED_VENDOR : SRCDOC_SANDBOX_LOCKED}
      title={title ?? 'Embedded content'}
      style={{
        height: resolveInitialIframeHeight(initialHeight),
        minHeight: `${MIN_EMBED_HEIGHT_PX}px`,
        overflow: 'hidden',
      }}
    />
  );
}

function EmbedContent({
  html,
  url,
  interactive = true,
}: {
  html?: string;
  url?: string;
  interactive?: boolean;
}) {
  const hasMounted = useMounted();
  const isPageEditor = useIsPageEditor();
  const dragThumbnailClassName = getDragThumbnailClassName({ isPageEditor });
  const normalizedUrl = url ? normalizeEmbedUrl(url) : '';
  const hasHtml = Boolean(html?.trim());
  const hasUrl = Boolean(normalizedUrl);

  if (!hasHtml && !hasUrl) {
    return interactive ? null : <EmbedPlaceholder message="Embed content" />;
  }

  if (!hasMounted) {
    return <EmbedPlaceholder message="Loading embed" />;
  }

  if (hasUrl && isSafeEmbedUrl(normalizedUrl)) {
    return (
      <InjectedIframeEmbed
        attrs={{ src: normalizedUrl, allowfullscreen: '' }}
        declaredHeight={undefined}
        providerFallbackHeight={readExternalEmbedFallbackHeight(normalizedUrl)}
        interactive={interactive}
        isPageEditor={isPageEditor}
        dragThumbnailClassName={dragThumbnailClassName}
      />
    );
  }

  if (!html) {
    return hasUrl ? <EmbedPlaceholder message="Embed content" /> : null;
  }

  const parsedIframeEmbed = parseIframeEmbed(html);
  if (parsedIframeEmbed?.kind === 'srcDoc') {
    const trustedSrcDoc = normalizeTrustedElfsightHtml(parsedIframeEmbed.html);
    return (
      <div
        className={cn(
          'embed-block w-full overflow-hidden rounded-md [&>iframe]:w-full [&>iframe]:rounded-md',
          dragThumbnailClassName,
        )}
      >
        <SrcDocIframe
          html={trustedSrcDoc ?? parsedIframeEmbed.html}
          interactive={interactive}
          initialHeight={parsedIframeEmbed.height}
          title={parsedIframeEmbed.title}
          allowSameOrigin={trustedSrcDoc != null}
        />
      </div>
    );
  }

  if (parsedIframeEmbed?.kind === 'external') {
    return (
      <InjectedIframeEmbed
        attrs={parsedIframeEmbed.attrs}
        declaredHeight={parsedIframeEmbed.declaredHeight}
        providerFallbackHeight={parsedIframeEmbed.providerFallbackHeight}
        interactive={interactive}
        isPageEditor={isPageEditor}
        dragThumbnailClassName={dragThumbnailClassName}
      />
    );
  }

  const trustedHtml = normalizeTrustedElfsightHtml(html);
  return (
    <div
      className={cn(
        'embed-block w-full overflow-hidden rounded-md [&>iframe]:w-full [&>iframe]:rounded-md',
        dragThumbnailClassName,
      )}
    >
      <SrcDocIframe
        html={trustedHtml ?? html}
        interactive={interactive}
        allowSameOrigin={trustedHtml != null}
      />
    </div>
  );
}

export const EmbedBlock = memo(function EmbedBlock({
  html,
  resolvedHtml,
  url,
  interactive = true,
}: EmbedBlockProps) {
  return <EmbedContent html={resolvedHtml ?? html} url={url} interactive={interactive} />;
});
